import { HomographyCamera } from '../actions/homographyActions';
import { Config } from '../config';
import { Event } from '../interfaces';
import { CalibrationType, XyPoint } from '../interfaces/homography';
import { getEventLPRImageSize } from './getEventVideos';

const colorMap = new Map<number, string>([
  [0, 'red'],
  [1, 'blue'],
  [2, 'green'],
  [3, '#ff9100'],
  [4, '#c0c000'],
]);

/**
 * Returns the inverse matrix of a given homography matrix
 * @param {Float32Array} homography - m-sized (known) homography array
 * @param {Float32Array} inverted - n-sized [0] array, where n == m
 */

const inverse = (homography: Float32Array, inverted: Float32Array) => {
  // determinant of original matrix
  const det =
    homography[0] * (homography[4] * homography[8] - homography[5] * homography[7]) -
    homography[1] * (homography[3] * homography[8] - homography[5] * homography[6]) +
    homography[2] * (homography[3] * homography[7] - homography[4] * homography[6]);

  // minors
  inverted[0] = homography[4] * homography[8] - homography[5] * homography[7];
  inverted[1] = homography[3] * homography[8] - homography[5] * homography[6];
  inverted[2] = homography[3] * homography[7] - homography[4] * homography[6];
  inverted[3] = homography[1] * homography[8] - homography[2] * homography[7];
  inverted[4] = homography[0] * homography[8] - homography[2] * homography[6];
  inverted[5] = homography[0] * homography[7] - homography[1] * homography[6];
  inverted[6] = homography[1] * homography[5] - homography[2] * homography[4];
  inverted[7] = homography[0] * homography[5] - homography[2] * homography[3];
  inverted[8] = homography[0] * homography[4] - homography[1] * homography[3];

  // cofractors
  inverted[1] = -inverted[1];
  inverted[3] = -inverted[3];
  inverted[5] = -inverted[5];
  inverted[7] = -inverted[7];

  // adjugate
  // std::swap(inverted[1], inverted[3]);
  [inverted[1], inverted[3]] = [inverted[3], inverted[1]];
  // std::swap(inverted[2], inverted[6]);
  [inverted[2], inverted[6]] = [inverted[6], inverted[2]];
  // std::swap(inverted[5], inverted[7]);
  [inverted[5], inverted[7]] = [inverted[7], inverted[5]];

  // multiply by 1/det
  inverted[0] = (inverted[0] * 1.0) / det;
  inverted[1] = (inverted[1] * 1.0) / det;
  inverted[2] = (inverted[2] * 1.0) / det;
  inverted[3] = (inverted[3] * 1.0) / det;
  inverted[4] = (inverted[4] * 1.0) / det;
  inverted[5] = (inverted[5] * 1.0) / det;
  inverted[6] = (inverted[6] * 1.0) / det;
  inverted[7] = (inverted[7] * 1.0) / det;
  inverted[8] = (inverted[8] * 1.0) / det;
};

/**
 * haversine distance of two {lat,lng} points.
 * @param {google.maps.LatLng} pointOne - initial point
 * @param {google.maps.LatLng} pointTwo - end point
 * @returns - distance in meters
 */
const haversineDistanceGoogleMaps = (pointOne: google.maps.LatLng, pointTwo: google.maps.LatLng): number => {
  const earthRadius = 6371 * 1000; // Radius of the earth in meters
  const deltaLatitude = degreesToradius(pointTwo.lat() - pointOne.lat());
  const deltaLongitude = degreesToradius(pointTwo.lng() - pointOne.lng());
  const a =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(degreesToradius(pointOne.lat())) *
      Math.cos(degreesToradius(pointTwo.lat())) *
      Math.sin(deltaLongitude / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
}; // c = sqrt(a**2 + b**2)

/**
 * Transform given point distance into a coordinate plane zeroed on a given heading
 * @param pointOne - initial point
 * @param pointTwo - end point
 * @param heading - heading (degrees)
 * @returns
 */
export const latLongDistance = (
  pointOne: google.maps.LatLng,
  pointTwo: google.maps.LatLng,
  heading: number,
): [number, number] => {
  const pointTwoPrime = new google.maps.LatLng({
    lat: pointOne.lat(),
    lng: pointTwo.lng(),
  } as google.maps.LatLngLiteral);
  const delX = haversineDistanceGoogleMaps(pointOne, pointTwoPrime) * (pointOne.lng() > pointTwoPrime.lng() ? -1 : 1);
  const delY = haversineDistanceGoogleMaps(pointTwoPrime, pointTwo) * (pointTwoPrime.lat() > pointTwo.lat() ? -1 : 1);
  return transformToHeading(heading, delX, delY);
};

/**
 * returns a new point (google.maps.LatLngLiteral) based on current location, and a point {x, y} meters away
 * @param {{lat: number, lng: number}} initalLocation - initial point location
 * @param {{x: number, y: number}} xyPoint - new point coordinate in X, Y (meters)
 * @param {number} deviceHeading - heading in degrees of the current device
 * @returns {{lat: number, lng: number}} - new google maps LatLng literal
 */
export const solveForNewLatLng = (
  initialLocation: google.maps.LatLngLiteral,
  xyPoint: XyPoint,
  deviceHeading: number,
): google.maps.LatLngLiteral => {
  const earthRadius = 6378137.0; // 6371.0088 * 1000; // Radius of the earth in meters
  const bearing = directProblemGetBearing(xyPoint, deviceHeading);
  const distanceAsRadians = Math.sqrt(xyPoint.x ** 2 + xyPoint.y ** 2) / earthRadius; // radian representation of the distance travled.
  const latitudePrime = directProblemLatitudeSolver(initialLocation.lat, bearing, distanceAsRadians);
  const longitudePrime = directProblemLongitudeSolver(
    latitudePrime,
    initialLocation.lat,
    initialLocation.lng,
    bearing,
    distanceAsRadians,
  );

  return { lat: latitudePrime, lng: longitudePrime } as google.maps.LatLngLiteral;
};

/**
 * solves for the resulting latitude given curent latitude, bearing, and a distance (radians)
 * @param { number } initialLatitude
 * @param { number } bearing - in degrees
 * @param { number } ditance - radians (EarthRadius/point distance)
 * @returns {number} - new latitude in degrees
 */
const directProblemLatitudeSolver = (initialLatitude: number, bearing: number, distance: number): number => {
  const halfCircle = 180;
  const latitudeInRadians = (initialLatitude * Math.PI) / halfCircle;
  return (
    (Math.asin(
      Math.sin(latitudeInRadians) * Math.cos(distance) +
        Math.cos(latitudeInRadians) * Math.sin(distance) * Math.cos((bearing * Math.PI) / halfCircle),
    ) *
      180) /
    Math.PI
  );
};

/**
 * solves for the resulting longitude given resulting latitudem current latitude, current longitude, bearing, and distance (radians),
 * @param { number } latitudePrime - the resultant latitude for the new point
 * @param { number } initialLatidude - initial latitude
 * @param { number } initialLongitude - initial longitude
 * @param { number } bearing - bearing to new point
 * @param { number } distance - distance in radians
 * @returns { number } - new longitude in degrees
 */
const directProblemLongitudeSolver = (
  latitudePrime: number,
  initialLatidude: number,
  initialLongitude: number,
  bearing: number,
  distance: number,
): number => {
  const halfCircle = 180;
  const initialLatitudeRads = (initialLatidude * Math.PI) / halfCircle;
  const latitudePrimeRads = (latitudePrime * Math.PI) / halfCircle;

  const yPrime = Math.sin((bearing * Math.PI) / halfCircle) * Math.sin(distance) * Math.cos(initialLatitudeRads);
  const xPrime = Math.cos(distance) - Math.sin(initialLatitudeRads) * Math.sin(latitudePrimeRads);

  return initialLongitude + (Math.atan2(yPrime, xPrime) * 180) / Math.PI;
};

/**
 * returns the bearing towards the target point
 * @param { {x: number, y: number} } xyPoint - destination point in meters
 * @param {number} deviceHeading - device heading in degrees
 * @returns { number }- heading towards new point
 */
const directProblemGetBearing = (xyPoint: XyPoint, deviceHeading: number): number => {
  // points are relative to device heading, therefore x & y are reversed vs the cartessian plane
  return (Math.atan2(xyPoint.x, xyPoint.y) * (180 / Math.PI) + deviceHeading) % 360;
};

/**
 * function to get A,B (x, y) distance given a heading (think pythagoras c^2 = a^2 + b^2)
 * @param {number} heading - degrees
 * @param {number} x - distance in meters - North; using default map heading
 * @param {number} y - distance in meters - East; using default map heading
 * @returns {[number, number]} - converted distances
 */
const transformToHeading = (heading: number, x: number, y: number): [number, number] => {
  const theta = -1 * degreesToradius(heading);
  const xPrime = x * Math.cos(theta) + y * Math.sin(theta);
  const yPrime = -1 * x * Math.sin(theta) + y * Math.cos(theta);
  return [xPrime, yPrime];
};

const degreesToradius = (degree: number) => {
  return degree * (Math.PI / 180);
};

export const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

/**
 * custom homography function to convert any coordinates into another plane
 * @param {Float32Array} homography - the homography matrix (non-inverted)
 * @param {number[]} flatArrayOfCoords - array of points in the form [x1, y1, x2, y2, ...] to be transformed
 * @returns {number[][]} - array of transformed coordinates [[x1, y1], [x2, y2], ...]
 */
export const transformPointsToContextCameraCoordinates = (
  homography: Float32Array,
  flatArrayOfCoords: number[],
): number[][] => {
  const points: XyPoint[] = [];
  for (let i = 0; i <= flatArrayOfCoords.length - 1; i += 2) {
    points.push({ x: flatArrayOfCoords[i], y: flatArrayOfCoords[i + 1] });
  }

  const homography_iv = new Float32Array(9);
  inverse(homography, homography_iv);
  const points_iv: number[][] = [];
  for (let i = 0; i < points.length; i++) {
    const { x, y } = multiply(homography_iv, points[i]);
    points_iv[i] = [x, y];
  }

  return points_iv;
};

/**
 * specialized homography function to convert coordinate planes into context camera pixle coordinates
 * @param {Float32Array} homography - the homography matrix
 * @param {CalibrationType} calibrationType
 * @returns {number[][]} - array of transformed coordinates
 */
export const calculateContextCameraCoordinates = (
  homography: Float32Array,
  event?: Event,
  calibrationType?: CalibrationType,
): number[][] => {
  if (calibrationType === CalibrationType.MAP) {
    return transformPointsToContextCameraCoordinates(
      homography,
      Config.AppDefaults.Homography.mapHomographyBoundingPoints.flat(),
    );
  }

  // Map entire LPR frustrum onto context camera view
  const size = getEventLPRImageSize(event);
  const contextStandardPoints = [0, 0, 0, size.height, size.width, size.height, size.width, 0];

  return transformPointsToContextCameraCoordinates(homography, contextStandardPoints);
};

const multiply = (mat: Float32Array, point: XyPoint) => {
  const out = { x: 0, y: 0 };

  const coeff = 1.0 / (mat[6] * point.x + mat[7] * point.y + mat[8]);
  out.x = coeff * (mat[0] * point.x + mat[1] * point.y + mat[2]);
  out.y = coeff * (mat[3] * point.x + mat[4] * point.y + mat[5]);

  return out;
};

export const getLicensePlateBounds = (plateCrop: string): { x: number; y: number }[] => {
  const coords = plateCrop.split(';').map((v) => parseInt(v, 10));

  const points = [];
  for (let i = 0; i <= coords.length - 1; i += 2) {
    points.push({ x: coords[i], y: coords[i + 1] });
  }

  return points;
};

export const calculatePoint = (homography: Float32Array, plateCrop: string) => {
  const points = getLicensePlateBounds(plateCrop);

  const homography_iv = new Float32Array(9);
  inverse(homography, homography_iv);
  const points_iv = [];
  for (let i = 0; i < points.length; i++) {
    points_iv[i] = multiply(homography_iv, points[i]);
  }

  let x_mid = 0;
  let y_mid = 0;

  for (let i = 0; i < points_iv.length; i++) {
    x_mid += points_iv[i].x;
    y_mid += points_iv[i].y;
  }

  return { x: x_mid / 4, y: y_mid / 4 - 30 };
};

export const getPinColor = (pinIndex: number): string => {
  return colorMap.get(pinIndex % colorMap.size);
};

export const getPartnerPoints = (calibrationType: CalibrationType, cameraType: HomographyCamera): HomographyCamera => {
  if (calibrationType === CalibrationType.MAP) {
    if (cameraType === HomographyCamera.MAP) {
      return HomographyCamera.CONTEXT;
    }
    return HomographyCamera.MAP;
  }
  if (cameraType === HomographyCamera.CONTEXT) {
    return HomographyCamera.LPR;
  }
  return HomographyCamera.CONTEXT;
};
